/* eslint-disable no-param-reassign */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module';
import { srgbColor } from './geometriaHelpers';
import { buildCurrentBolt, buildFanUnit, FAN_SCALE } from './transformadorProcedural';

const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('/static/draco/');
// Baixa o decoder draco em paralelo com o mount, antes do GLTFLoader
// precisar dele (ver docs/specs/cma-transformador-cad-performance.md §2).
dracoLoader.preload();

const COR = 0x6a6f74;
const RUGOSIDADE = 0.62;

const PART_MATERIAL_CAD = {
  Body: 'tanque',
  Online_DGA: 'escuro',
  Top_Oil_Temp: 'chapa',
  Power_pipe_S: 'prateado',
  Power_pipe_M: 'prateado',
  pipe_B: 'prateado',
  Wire_B: 'prateado',
  Wire_M: 'prateado',
  iron_S: 'prateado',
  Nut: 'prateado',
};

const PECAS_BUCHA_CAD = [
  'Bushing_B1', 'Bushing_B2', 'Bushing_B3',
  'Bushing_S1', 'Bushing_S2', 'Bushing_S3', 'Bushing_M1',
  'Bushing_mini1', 'Bushing_mini2', 'Bushing_mini3',
  // 717/718/719 = fileira de cima (buchas de alta, recebem raio — ver
  // PECAS_ELETRICIDADE_CAD); 664/665/666/720 = fileira de baixo (sem raio).
  'Out_polySurface717', 'Out_polySurface718', 'Out_polySurface719',
  'Out_polySurface664', 'Out_polySurface665', 'Out_polySurface666',
  'Out_polySurface720',
];

const PECAS_ELETRICIDADE_CAD = [
  'Bushing_S1', 'Bushing_S2', 'Bushing_S3', 'Bushing_M1',
  'Bushing_mini1', 'Bushing_mini2', 'Bushing_mini3',
  // Fileira de cima (buchas de alta, ver PECAS_BUCHA_CAD) — a de baixo não recebe raio.
  'Out_polySurface717', 'Out_polySurface718', 'Out_polySurface719',
];

const RAIO_ALTURA_REFERENCIA = 2.5;

const UV_VERTICES_POR_FATIA = 200000;

/**
 * Projeta UV em metros de cena (ver aplicarUvDeMundo, cmaAssetTextures.js).
 * Generator: cede o controle a cada UV_VERTICES_POR_FATIA vértices para não
 * travar a thread principal numa malha de milhões de triângulos (ver
 * executarEmFatias).
 */
function* aplicarUvEscaladoPorEtapas(geometry, escala) {
  const posicao = geometry.getAttribute('position');
  const normal = geometry.getAttribute('normal');
  if (!posicao || !normal) return;

  const totalVertices = posicao.count;
  const posArray = posicao.array;
  const normArray = normal.array;
  const uv = new Float32Array(totalVertices * 2);

  for (let i = 0; i < totalVertices; i += 1) {
    const indice = i * 3;
    const px = posArray[indice] * escala;
    const py = posArray[indice + 1] * escala;
    const pz = posArray[indice + 2] * escala;
    const nx = Math.abs(normArray[indice]);
    const ny = Math.abs(normArray[indice + 1]);
    const nz = Math.abs(normArray[indice + 2]);

    let u;
    let v;
    if (nx >= ny && nx >= nz) {
      u = pz;
      v = py;
    } else if (ny >= nx && ny >= nz) {
      u = px;
      v = pz;
    } else {
      u = px;
      v = py;
    }
    uv[i * 2] = u;
    uv[i * 2 + 1] = v;

    if ((i + 1) % UV_VERTICES_POR_FATIA === 0) yield;
  }

  const atributo = new THREE.BufferAttribute(uv, 2);
  geometry.setAttribute('uv', atributo);
  geometry.setAttribute('uv2', atributo);
}

// Sem vertexColors o shader dos materiais texturizados lê preto —
// cor branca neutra satisfaz o shader.
function aplicarCorNeutra(geometry) {
  if (geometry.getAttribute('color')) return geometry;
  const posicao = geometry.getAttribute('position');
  if (!posicao) return geometry;
  geometry.setAttribute('color', new THREE.BufferAttribute(
    new Float32Array(posicao.count * 3).fill(1), 3,
  ));
  return geometry;
}

const ANCHORS_CAD = [
  {
    id: 'buchas',
    // Bushing_B1/2/3 = nomes antigos; Out_polySurface717/718/719 = mesmos
    // nós no GLB atual (fileira de cima).
    nomes: ['Bushing_B1', 'Bushing_B2', 'Bushing_B3', 'Out_polySurface717', 'Out_polySurface718', 'Out_polySurface719'],
  },
  // Chamava-se 'tensaoCarga' — é a âncora do card 'Temperaturas' (ver CmaDashboard.vue).
  { id: 'temperaturas', nomes: ['Wire_M'] },
  { id: 'bolsa', nomes: ['Top_Oil_Temp'] },
  {
    id: 'tensao',
    nomes: [
      'Bushing_S1', 'Bushing_S2', 'Bushing_S3', 'Bushing_M1',
      'Bushing_mini1', 'Bushing_mini2', 'Bushing_mini3',
      // Fileira de baixo — ver PECAS_BUCHA_CAD.
      'Out_polySurface664', 'Out_polySurface665', 'Out_polySurface666', 'Out_polySurface720',
    ],
  },
  { id: 'eletricos', nomes: ['Online_DGA'] },
  { id: 'comutador', nomes: ['Online_DGA'] },
  // Sem nó no GLB — sobrescritas por fixarAncora após montarNaCena; ficam vazias até lá.
  { id: 'resfriamento', nomes: [] },
  { id: 'gas', nomes: [] },
];

const PECA_BASE = 'Body';

const PECAS_PENDENTES = ['pipe_B'];

// Nós reais de transformador-tfmp.glb (nomes genéricos do Maya).
// FloatingFanBank/FloatingFanBank2 são exceção: bancos de ventilador
// procedurais (ver criarBancoVentiladores), incluídos aqui só para editar a
// direção de explosão junto com as demais peças.
const PART_EXPLODE_CAD = {
  FloatingFanBank: [1, 0, -11],
  FloatingFanBank2: [1, 0, -11],
  Out_AB01_AB9_pCylinder14: [1, 0, 11],
  // [0, 0, 0] de propósito: fica parada na explosão (ver EXPLODE_GROUP_ORDER
  // em CmaAsset3dView.vue, que não lista mais esta peça).
  Out_group1_pCylinder40: [0, 0, 0],
  // Grupo do TANQUE (226 peças, todas com este vetor) — [0, 0, 0] de
  // propósito, o tanque não se separa na explosão (ver EXPLODE_GROUP_ORDER
  // em CmaAsset3dView.vue, que não lista mais este grupo).
  Out_group10_polySurface469: [0, 0, 0],
  Out_group10_polySurface470: [0, 0, 0],
  Out_group10_polySurface471: [0, 0, 0],
  Out_group10_polySurface472: [0, 0, 0],
  Out_group17_pCube308: [0, 0, 0],
  Out_group17_polySurface463: [0, 0, 0],
  Out_Group_005: [0.5, 0, 1],
  Out_Group_007: [0.5, 0, 1],
  Out_Group_008: [0.5, 0, 1],
  Out_pCube1: [0, 0, 0],
  Out_pCube12: [1, 0, 11],
  Out_pCube28: [1, 0, 11],
  Out_pCube29: [1, 0, 11],
  Out_pCube30: [1, 0, 11],
  Out_pCube31: [1, 0, 11],
  Out_pCube32: [1, 0, 11],
  Out_pCube33: [1, 0, 11],
  Out_pCube39: [1, 0, 11],
  Out_pCube47: [0, 0, 0],
  Out_pCube48: [1, 0, 11],
  Out_pCube49: [1, 0, 11],
  Out_pCube50: [1, 0, 11],
  Out_pCube51: [1, 0, 11],
  Out_pCube52: [1, 0, 11],
  Out_pCube53: [1, 0, 11],
  Out_pCube54: [1, 0, 11],
  Out_pCube55: [1, 0, 11],
  Out_pCube56: [1, 0, 11],
  Out_pCube57: [1, 0, 11],
  Out_pCube58: [1, 0, 11],
  Out_pCube59: [1, 0, 11],
  Out_pCube60: [1, 0, 11],
  Out_pCube61: [1, 0, 11],
  Out_pCube62: [1, 0, 11],
  Out_pCube63: [1, 0, 11],
  Out_pCube64: [1, 0, 11],
  Out_pCube65: [1, 0, 11],
  Out_pCube66: [1, 0, 11],
  Out_pCube67: [1, 0, 11],
  Out_pCube68: [1, 0, 11],
  Out_pCube69: [1, 0, 11],
  Out_pCube70: [1, 0, 11],
  Out_pCube71: [1, 0, 11],
  Out_pCube72: [1, 0, 11],
  Out_pCube78: [1, 0, 11],
  Out_pCube79: [1, 0, 11],
  Out_pCube80: [1, 0, 11],
  Out_pCube81: [1, 0, 11],
  Out_pCube83: [0, 0, 0],
  // Grupo BASE (8 peças) — só desce (Y), sem deslocar em X nem Z.
  Out_pCube84: [0, -2, 0],
  Out_pCube87: [0, 0, 0],
  Out_pCube88: [0, 0, 0],
  Out_pCube93: [1, 0, 11],
  Out_pCube94: [1, 0, 11],
  Out_pCube95: [1, 0, 11],
  Out_pCube96: [1, 0, 11],
  Out_pCube97: [1, 0, 11],
  Out_pCube98: [1, 0, 11],
  Out_pCube99: [1, 0, 11],
  Out_pCube100: [1, 0, 11],
  Out_pCube101: [1, 0, 11],
  Out_pCube102: [1, 0, 11],
  Out_pCube103: [1, 0, 11],
  Out_pCube104: [1, 0, 11],
  Out_pCube105: [1, 0, 11],
  Out_pCube106: [1, 0, 11],
  Out_pCube107: [1, 0, 11],
  Out_pCube108: [1, 0, 11],
  Out_pCube109: [1, 0, 11],
  Out_pCube110: [1, 0, 11],
  Out_pCube111: [1, 0, 11],
  Out_pCube112: [1, 0, 11],
  Out_pCube113: [1, 0, 11],
  Out_pCube114: [1, 0, 11],
  Out_pCube115: [1, 0, 11],
  Out_pCube116: [1, 0, 11],
  Out_pCube117: [1, 0, 11],
  Out_pCube118: [1, 0, 11],
  Out_pCube119: [1, 0, 11],
  Out_pCube120: [1, 0, 11],
  Out_pCube121: [1, 0, 11],
  Out_pCube122: [1, 0, 11],
  Out_pCube123: [1, 0, 11],
  Out_pCube124: [1, 0, 11],
  Out_pCube125: [1, 0, 11],
  Out_pCube126: [1, 0, 11],
  Out_pCube127: [1, 0, 11],
  Out_pCube128: [1, 0, 11],
  Out_pCube129: [0, 0, 0],
  Out_pCube130: [1, 0, 11],
  Out_pCube131: [1, 0, 11],
  Out_pCube132: [1, 0, 11],
  Out_pCube133: [1, 0, 11],
  Out_pCube134: [1, 0, 11],
  Out_pCube135: [1, 0, 11],
  Out_pCube136: [1, 0, 11],
  Out_pCube137: [1, 0, 11],
  Out_pCube138: [1, 0, 11],
  Out_pCube139: [1, 0, 11],
  Out_pCube140: [1, 0, 11],
  Out_pCube141: [1, 0, 11],
  Out_pCube142: [1, 0, 11],
  Out_pCube143: [1, 0, 11],
  Out_pCube144: [1, 0, 11],
  Out_pCube145: [1, 0, 11],
  Out_pCube146: [1, 0, 11],
  Out_pCube147: [1, 0, 11],
  Out_pCube148: [1, 0, 11],
  Out_pCube149: [1, 0, 11],
  Out_pCube150: [1, 0, 11],
  Out_pCube151: [1, 0, 11],
  Out_pCube152: [1, 0, 11],
  Out_pCube153: [1, 0, 11],
  Out_pCube154: [1, 0, 11],
  Out_pCube155: [1, 0, 11],
  Out_pCube156: [1, 0, 11],
  Out_pCube157: [1, 0, 11],
  Out_pCube158: [1, 0, 11],
  Out_pCube159: [1, 0, 11],
  Out_pCube160: [1, 0, 11],
  Out_pCube161: [1, 0, 11],
  Out_pCube162: [1, 0, 11],
  Out_pCube163: [1, 0, 11],
  Out_pCube164: [1, 0, 11],
  Out_pCube165: [1, 0, 11],
  Out_pCube166: [1, 0, 11],
  Out_pCube167: [1, 0, 11],
  Out_pCube168: [1, 0, 11],
  Out_pCube169: [1, 0, 11],
  Out_pCube170: [1, 0, 11],
  Out_pCube171: [1, 0, 11],
  Out_pCube172: [1, 0, 11],
  Out_pCube173: [1, 0, 11],
  Out_pCube174: [1, 0, 11],
  Out_pCube175: [1, 0, 11],
  Out_pCube176: [1, 0, 11],
  Out_pCube177: [1, 0, 11],
  Out_pCube178: [1, 0, 11],
  Out_pCube179: [1, 0, 11],
  Out_pCube180: [1, 0, 11],
  Out_pCube181: [1, 0, 11],
  Out_pCube182: [1, 0, 11],
  Out_pCube183: [1, 0, 11],
  Out_pCube184: [1, 0, 11],
  Out_pCube185: [1, 0, 11],
  Out_pCube186: [1, 0, 11],
  Out_pCube187: [1, 0, 11],
  Out_pCube188: [1, 0, 11],
  Out_pCube189: [1, 0, 11],
  Out_pCube190: [1, 0, 11],
  Out_pCube191: [1, 0, 11],
  Out_pCube192: [1, 0, 11],
  Out_pCube193: [1, 0, 11],
  Out_pCube194: [1, 0, 11],
  Out_pCube195: [1, 0, 11],
  Out_pCube196: [1, 0, 11],
  Out_pCube197: [1, 0, 11],
  Out_pCube198: [1, 0, 11],
  Out_pCube199: [1, 0, 11],
  Out_pCube200: [1, 0, 11],
  Out_pCube201: [1, 0, 11],
  Out_pCube202: [1, 0, 11],
  Out_pCube203: [1, 0, 11],
  Out_pCube204: [1, 0, 11],
  Out_pCube205: [1, 0, 11],
  Out_pCube206: [1, 0, 11],
  Out_pCube207: [1, 0, 11],
  Out_pCube208: [1, 0, 11],
  Out_pCube209: [1, 0, 11],
  Out_pCube210: [1, 0, 11],
  Out_pCube211: [1, 0, 11],
  Out_pCube212: [1, 0, 11],
  Out_pCube213: [1, 0, 11],
  Out_pCube214: [1, 0, 11],
  Out_pCube215: [1, 0, 11],
  Out_pCube216: [1, 0, 11],
  Out_pCube217: [1, 0, 11],
  Out_pCube218: [1, 0, 11],
  Out_pCube219: [1, 0, 11],
  Out_pCube220: [1, 0, 11],
  Out_pCube221: [1, 0, 11],
  Out_pCube222: [1, 0, 11],
  Out_pCube223: [1, 0, 11],
  Out_pCube224: [1, 0, 11],
  Out_pCube225: [1, 0, 11],
  Out_pCube226: [1, 0, 11],
  Out_pCube227: [1, 0, 11],
  Out_pCube228: [1, 0, 11],
  Out_pCube229: [1, 0, 11],
  Out_pCube230: [1, 0, 11],
  Out_pCube231: [1, 0, 11],
  Out_pCube232: [1, 0, 11],
  Out_pCube233: [1, 0, 11],
  Out_pCube234: [1, 0, 11],
  Out_pCube235: [1, 0, 11],
  Out_pCube236: [1, 0, 11],
  Out_pCube237: [1, 0, 11],
  Out_pCube238: [1, 0, 11],
  Out_pCube239: [1, 0, 11],
  Out_pCube240: [1, 0, 11],
  Out_pCube241: [1, 0, 11],
  Out_pCube242: [1, 0, 11],
  Out_pCube243: [1, 0, 11],
  Out_pCube244: [1, 0, 11],
  Out_pCube245: [1, 0, 11],
  Out_pCube246: [1, 0, 11],
  Out_pCube247: [1, 0, 11],
  Out_pCube248: [1, 0, 11],
  Out_pCube249: [1, 0, 11],
  Out_pCube250: [1, 0, 11],
  Out_pCube251: [1, 0, 11],
  Out_pCube252: [1, 0, 11],
  Out_pCube253: [1, 0, 11],
  Out_pCube254: [1, 0, 11],
  Out_pCube255: [1, 0, 11],
  Out_pCube256: [1, 0, 11],
  Out_pCube257: [1, 0, 11],
  Out_pCube258: [1, 0, 11],
  Out_pCube259: [1, 0, 11],
  Out_pCube260: [1, 0, 11],
  Out_pCube261: [1, 0, 11],
  Out_pCube262: [1, 0, 11],
  Out_pCube263: [1, 0, 11],
  Out_pCube264: [1, 0, 11],
  Out_pCube265: [1, 0, 11],
  Out_pCube266: [1, 0, 11],
  Out_pCube267: [1, 0, 11],
  Out_pCube268: [1, 0, 11],
  Out_pCube269: [1, 0, 11],
  // Grupo BOLSA (79 peças) — só sobe (Y), sem deslocar em X nem Z.
  Out_pCube275: [0, 2, 0],
  Out_pCube276: [0, 2, 0],
  Out_pCube280: [0, 2, 0],
  Out_pCube281: [0, 2, 0],
  Out_pCube284: [0, 2, 0],
  Out_pCube285: [0, 2, 0],
  Out_pCube286: [0, 2, 0],
  Out_pCube287: [0, 2, 0],
  Out_pCube288: [0, 2, 0],
  Out_pCube289: [0, 2, 0],
  Out_pCube291: [0, 0, 0],
  Out_pCube292: [0, 0, 0],
  Out_pCube293: [0, 0, 0],
  Out_pCube294: [0, 0, 0],
  Out_pCube295: [0, 0, 0],
  Out_pCube296: [0, 0, 0],
  Out_pCube297: [0, 0, 0],
  Out_pCube298: [0, 0, 0],
  Out_pCube302: [1, 0, 11],
  Out_pCube303: [1, 0, 11],
  Out_pCube307: [0, 0, 0],
  Out_pCube308: [0, 0, 0],
  Out_pCube309: [0, 0, 0],
  Out_pCube310: [0, 0, 0],
  Out_pCube312: [0, 0, 0],
  Out_pCube315: [0, 0, 0],
  Out_pCube316: [0, 0, 0],
  Out_pCube329: [0, -2, 0],
  Out_pCube330: [0, -2, 0],
  Out_pCube331: [0, -2, 0],
  Out_pCube332: [0, -2, 0],
  Out_pCube333: [0, -2, 0],
  Out_pCube334: [0, -2, 0],
  Out_pCube349: [0, 0, 0],
  Out_pCube351: [0, 0, 0],
  Out_pCube352: [4, 0, 1],
  Out_pCube353: [4, 0, 1],
  Out_pCube354: [4, 0, 1],
  Out_pCube355: [4, 0, 1],
  Out_pCube356: [4, 0, 1],
  Out_pCube357: [4, 0, 1],
  Out_pCube358: [4, 0, 1],
  Out_pCube359: [4, 0, 1],
  Out_pCube360: [0, 2, 0],
  Out_pCube361: [0, 2, 0],
  Out_pCube368: [4, 0, 1],
  Out_pCube375: [0, 2, 0],
  Out_pCylinder1: [1, 0, 11],
  Out_pCylinder4: [1, 0, 11],
  Out_pCylinder5: [1, 0, 11],
  Out_pCylinder6: [1, 0, 11],
  Out_pCylinder7: [1, 0, 11],
  Out_pCylinder8: [1, 0, 11],
  Out_pCylinder9: [1, 0, 11],
  Out_pCylinder10: [1, 0, 11],
  Out_pCylinder11: [1, 0, 11],
  Out_pCylinder12: [1, 0, 11],
  Out_pCylinder13: [1, 0, 11],
  Out_pCylinder15: [1, 0, 11],
  Out_pCylinder20: [1, 0, 11],
  Out_pCylinder38: [0, 0, 0],
  Out_pCylinder39: [0, 0, 0],
  Out_pCylinder40: [0, 0, 0],
  Out_pCylinder41: [1, 0, 11],
  Out_pCylinder42: [1, 0, 11],
  Out_pCylinder43: [1, 0, 11],
  Out_pCylinder44: [1, 0, 11],
  Out_pCylinder45: [1, 0, 11],
  Out_pCylinder46: [1, 0, 11],
  Out_pCylinder47: [1, 0, 11],
  Out_pCylinder48: [1, 0, 11],
  Out_pCylinder49: [1, 0, 11],
  Out_pCylinder50: [1, 0, 11],
  Out_pCylinder51: [1, 0, 11],
  Out_pCylinder52: [1, 0, 11],
  Out_pCylinder53: [1, 0, 11],
  Out_pCylinder54: [1, 0, 11],
  Out_pCylinder55: [1, 0, 11],
  Out_pCylinder56: [1, 0, 11],
  Out_pCylinder57: [1, 0, 11],
  Out_pCylinder58: [1, 0, 11],
  Out_pCylinder59: [1, 0, 11],
  Out_pCylinder60: [1, 0, 11],
  Out_pCylinder61: [1, 0, 11],
  Out_pCylinder62: [1, 0, 11],
  Out_pCylinder63: [1, 0, 11],
  Out_pCylinder64: [1, 0, 11],
  Out_pCylinder65: [1, 0, 11],
  Out_pCylinder66: [1, 0, 11],
  Out_pCylinder67: [1, 0, 11],
  Out_pCylinder68: [1, 0, 11],
  Out_pCylinder69: [1, 0, 11],
  Out_pCylinder70: [1, 0, 11],
  Out_pCylinder71: [1, 0, 11],
  Out_pCylinder72: [1, 0, 11],
  Out_pCylinder73: [1, 0, 11],
  Out_pCylinder74: [1, 0, 11],
  Out_pCylinder75: [1, 0, 11],
  Out_pCylinder76: [1, 0, 11],
  Out_pCylinder77: [1, 0, 11],
  Out_pCylinder78: [1, 0, 11],
  Out_pCylinder79: [1, 0, 11],
  Out_pCylinder80: [1, 0, 11],
  Out_pCylinder81: [1, 0, 11],
  Out_pCylinder82: [1, 0, 11],
  Out_pCylinder83: [1, 0, 11],
  Out_pCylinder84: [1, 0, 11],
  Out_pCylinder85: [1, 0, 11],
  Out_pCylinder86: [1, 0, 11],
  Out_pCylinder87: [1, 0, 11],
  Out_pCylinder88: [1, 0, 11],
  Out_pCylinder89: [1, 0, 11],
  Out_pCylinder90: [1, 0, 11],
  Out_pCylinder91: [1, 0, 11],
  Out_pCylinder92: [1, 0, 11],
  Out_pCylinder93: [1, 0, 11],
  Out_pCylinder94: [1, 0, 11],
  Out_pCylinder95: [1, 0, 11],
  Out_pCylinder96: [1, 0, 11],
  Out_pCylinder97: [1, 0, 11],
  Out_pCylinder98: [1, 0, 11],
  Out_pCylinder99: [1, 0, 11],
  Out_pCylinder100: [1, 0, 11],
  Out_pCylinder101: [1, 0, 11],
  Out_pCylinder102: [1, 0, 11],
  Out_pCylinder103: [1, 0, 11],
  Out_pCylinder104: [1, 0, 11],
  Out_pCylinder105: [1, 0, 11],
  Out_pCylinder106: [1, 0, 11],
  Out_pCylinder107: [1, 0, 11],
  Out_pCylinder108: [1, 0, 11],
  Out_pCylinder109: [1, 0, 11],
  Out_pCylinder110: [1, 0, 11],
  Out_pCylinder111: [1, 0, 11],
  Out_pCylinder112: [1, 0, 11],
  Out_pCylinder113: [1, 0, 11],
  Out_pCylinder114: [1, 0, 11],
  Out_pCylinder115: [1, 0, 11],
  Out_pCylinder116: [1, 0, 11],
  Out_pCylinder117: [1, 0, 11],
  Out_pCylinder118: [1, 0, 11],
  Out_pCylinder119: [1, 0, 11],
  Out_pCylinder120: [1, 0, 11],
  Out_pCylinder121: [1, 0, 11],
  Out_pCylinder122: [1, 0, 11],
  Out_pCylinder123: [1, 0, 11],
  Out_pCylinder124: [1, 0, 11],
  Out_pCylinder125: [1, 0, 11],
  Out_pCylinder126: [1, 0, 11],
  Out_pCylinder127: [1, 0, 11],
  Out_pCylinder128: [1, 0, 11],
  Out_pCylinder129: [1, 0, 11],
  Out_pCylinder130: [1, 0, 11],
  Out_pCylinder131: [1, 0, 11],
  Out_pCylinder132: [0, 2, 0],
  Out_pCylinder133: [0, 2, 0],
  Out_pCylinder134: [0, 2, 0],
  Out_pCylinder135: [1, 0, 11],
  Out_pCylinder138: [0.5, 0, 1],
  Out_pCylinder140: [0, 0, 0],
  Out_pCylinder141: [0, 2, 0],
  Out_pCylinder142: [0, 2, 0],
  Out_pCylinder143: [0, 2, 0],
  Out_pCylinder144: [0, 2, 0],
  Out_pCylinder145: [0, 2, 0],
  Out_pCylinder146: [0, 2, 0],
  Out_pCylinder147: [0, 2, 0],
  Out_pCylinder148: [0, 2, 0],
  Out_pCylinder150: [0, 2, 0],
  Out_pCylinder151: [0, 2, 0],
  Out_pCylinder152: [0, 2, 0],
  Out_pCylinder155: [0, 2, 0],
  Out_pCylinder160: [0, 2, 0],
  Out_pCylinder166: [0, 0, 0],
  Out_pCylinder167: [0, 0, 0],
  Out_pCylinder169_polySurface357: [0, 0, 0],
  Out_pCylinder170: [0, 0, 0],
  Out_pCylinder183: [4, 0, 1],
  Out_pCylinder184: [0, 0, 0],
  Out_pCylinder186: [0, 0, 0],
  Out_pCylinder187: [0, 0, 0],
  Out_pCylinder209: [4, 0, 1],
  Out_pCylinder210: [4, 0, 1],
  Out_pCylinder211: [0, 0, 0],
  Out_pCylinder225: [0, 2, 0],
  Out_pCylinder226: [0, 2, 0],
  Out_pCylinder227: [0, 2, 0],
  Out_pCylinder228: [0, 2, 0],
  Out_pCylinder229: [0, 2, 0],
  Out_pCylinder230: [0, 0, 0],
  Out_pCylinder231: [0, 0, 0],
  Out_pCylinder232: [0.5, 0, 1],
  Out_pCylinder233: [0.5, 0, 1],
  Out_pCylinder234: [0.5, 0, 1],
  Out_pCylinder235: [0, 2, 0],
  Out_pCylinder236: [0, 0, 0],
  Out_pCylinder237: [0, 2, 0],
  Out_pCylinder238: [0, 2, 0],
  Out_pCylinder239: [0, 0, 0],
  Out_pCylinder240: [0, 0, 0],
  Out_pCylinder241: [0, 0, 0],
  Out_pCylinder242: [0, 2, 0],
  Out_pCylinder243: [0, 2, 0],
  Out_pCylinder244: [0, 2, 0],
  Out_pCylinder245: [0, 2, 0],
  Out_pCylinder246: [0, 2, 0],
  Out_pCylinder247: [0, 2, 0],
  Out_pCylinder248: [0, 2, 0],
  Out_pCylinder249: [0, 2, 0],
  Out_pCylinder250: [0, 2, 0],
  Out_pCylinder251: [0, 2, 0],
  Out_pCylinder252: [0, 2, 0],
  Out_pCylinder255: [0, 2, 0],
  Out_pCylinder256: [0, 2, 0],
  Out_pCylinder257: [0, 2, 0],
  Out_pCylinder258: [0, 2, 0],
  Out_pCylinder259: [0, 0, 0],
  Out_pCylinder260: [0, 0, 0],
  Out_pCylinder261: [0, 0, 0],
  Out_pCylinder262: [0, 0, 0],
  Out_pCylinder263: [0, 0, 0],
  Out_pCylinder264: [0, 0, 0],
  Out_pCylinder265: [0, 0, 0],
  Out_pCylinder272: [4, 0, 1],
  Out_pCylinder275: [0, 0, 0],
  Out_pCylinder276: [0, 0, 0],
  Out_pCylinder277: [0, 0, 0],
  Out_pCylinder281: [0, 0, 0],
  Out_pCylinder284: [1, 0, 11],
  Out_pCylinder285: [1, 0, 11],
  Out_pCylinder286: [0, 0, 0],
  Out_pCylinder287: [0, 0, 0],
  Out_pCylinder288: [4, 0, 1],
  Out_pCylinder289: [4, 0, 1],
  Out_pCylinder290: [4, 0, 1],
  Out_pCylinder291: [4, 0, 1],
  Out_pCylinder292: [4, 0, 1],
  Out_pCylinder293: [0.5, 0, 1],
  Out_pCylinder294: [0.5, 0, 1],
  Out_pCylinder295: [0.5, 0, 1],
  Out_pCylinder296: [0.5, 0, 1],
  Out_pCylinder303: [0, 2, 0],
  Out_pCylinder313: [0, 2, 0],
  Out_pCylinder314: [0, 2, 0],
  Out_polySurface108: [1, 0, 11],
  Out_polySurface112: [1, 0, 11],
  Out_polySurface113: [1, 0, 11],
  Out_polySurface114: [1, 0, 11],
  Out_polySurface115: [1, 0, 11],
  Out_polySurface116: [1, 0, 11],
  Out_polySurface117: [1, 0, 11],
  Out_polySurface118: [1, 0, 11],
  Out_polySurface234: [0, -2, 0],
  Out_polySurface235: [1, 0, 11],
  Out_polySurface236: [1, 0, 11],
  Out_polySurface237: [1, 0, 11],
  Out_polySurface238: [1, 0, 11],
  Out_polySurface239: [1, 0, 11],
  Out_polySurface240: [1, 0, 11],
  Out_polySurface241: [1, 0, 11],
  Out_polySurface242: [1, 0, 11],
  Out_polySurface247: [0, 2, 0],
  Out_polySurface248: [0, 2, 0],
  Out_polySurface249: [0, 2, 0],
  Out_polySurface250: [0, 2, 0],
  Out_polySurface251: [0, 2, 0],
  Out_polySurface252: [0, 2, 0],
  Out_polySurface253: [0, 2, 0],
  Out_polySurface254: [0, 2, 0],
  Out_polySurface259: [0, 0, 0],
  Out_polySurface260: [0, 0, 0],
  Out_polySurface261: [0, 2, 0],
  Out_polySurface262: [0, 2, 0],
  Out_polySurface263: [0, 2, 0],
  Out_polySurface280: [0, 0, 0],
  Out_polySurface287: [0, 0, 0],
  Out_polySurface288: [0, 0, 0],
  Out_polySurface289: [1, 0, 11],
  Out_polySurface290: [1, 0, 11],
  Out_polySurface363: [0, 0, 0],
  Out_polySurface364: [0, 0, 0],
  Out_polySurface365: [0, 0, 0],
  Out_polySurface454: [0, 0, 0],
  Out_polySurface463: [0, 0, 0],
  Out_polySurface466: [0, 0, 0],
  Out_polySurface469: [0, 0, 0],
  Out_polySurface526: [0, 0, 0],
  Out_polySurface527: [0, 0, 0],
  // Grupo buchas-2 (3 peças) — zerado a pedido, ficava feio na explosão.
  Out_polySurface529: [0, 0, 0],
  Out_polySurface530: [0, 0, 0],
  Out_polySurface531: [0, 0, 0],
  Out_polySurface532: [0, 0, 0],
  Out_polySurface533: [0, 0, 0],
  Out_polySurface535: [0, 0, 0],
  Out_polySurface536: [0, 0, 0],
  Out_polySurface537: [0, 0, 0],
  Out_polySurface539: [0, 0, 0],
  Out_polySurface540: [0, 0, 0],
  Out_polySurface541: [0, 0, 0],
  Out_polySurface542: [0, 0, 0],
  Out_polySurface543: [0, 0, 0],
  Out_polySurface545: [0, 0, 0],
  Out_polySurface546: [0, 0, 0],
  Out_polySurface553: [0, 0, 0],
  Out_polySurface554: [0, 0, 0],
  Out_polySurface555: [0, 0, 0],
  Out_polySurface556: [4, 0, 1],
  Out_polySurface560: [0, 0, 0],
  Out_polySurface561: [0, 0, 0],
  Out_polySurface562: [0, 2, 0],
  Out_polySurface563: [0, 2, 0],
  Out_polySurface564: [0, 0, 0],
  Out_polySurface565: [0, 0, 0],
  Out_polySurface566: [0, 0, 0],
  Out_polySurface567: [0, 0, 0],
  Out_polySurface568: [0, 0, 0],
  Out_polySurface569: [0, 0, 0],
  Out_polySurface570: [0, 2, 0],
  Out_polySurface571: [0, 0, 0],
  Out_polySurface575: [0, 0, 0],
  Out_polySurface577: [0, 0, 0],
  Out_polySurface578: [0, 0, 0],
  Out_polySurface579: [0, 0, 0],
  Out_polySurface580: [0, 0, 0],
  Out_polySurface581: [0, 2, 0],
  Out_polySurface582: [0, 2, 0],
  Out_polySurface583: [0, 2, 0],
  Out_polySurface584: [0, 2, 0],
  Out_polySurface585: [0, 2, 0],
  Out_polySurface586: [0, 2, 0],
  Out_polySurface589: [0, 0, 0],
  Out_polySurface590: [0, 0, 0],
  Out_polySurface591: [0, 0, 0],
  Out_polySurface592: [0, 0, 0],
  Out_polySurface593: [0, 0, 0],
  Out_polySurface594: [0, 0, 0],
  Out_polySurface595: [0, 0, 0],
  Out_polySurface596: [0, 0, 0],
  Out_polySurface597: [0, 0, 0],
  Out_polySurface599: [0, 0, 0],
  Out_polySurface600: [0, 0, 0],
  Out_polySurface601: [0, 0, 0],
  Out_polySurface602: [0, 0, 0],
  Out_polySurface603: [0, 0, 0],
  Out_polySurface604: [4, 0, 1],
  Out_polySurface605: [0, 0, 0],
  Out_polySurface606: [0, 0, 0],
  Out_polySurface607: [0, 0, 0],
  Out_polySurface608: [0, 0, 0],
  Out_polySurface609: [0, 0, 0],
  Out_polySurface610: [0, 0, 0],
  Out_polySurface611: [0, 0, 0],
  Out_polySurface612: [0, 0, 0],
  Out_polySurface617: [4, 0, 1],
  Out_polySurface664: [1, 2, 1],
  Out_polySurface665: [1, 2, 1],
  Out_polySurface666: [1, 2, 1],
  Out_polySurface717: [1, 2, 1],
  Out_polySurface718: [1, 2, 1],
  Out_polySurface719: [1, 2, 1],
  Out_polySurface720: [1, 2, 1],
  Out_polySurface721: [0, 2, 0],
  Out_polySurface722: [0, 2, 0],
  Out_polySurface724: [0, 0, 0],
  Out_polySurface726: [0, 0, 0],
  Out_polySurface728: [0, 0, 0],
  Out_polySurface730: [0, 0, 0],
  Out_polySurface732: [0, 0, 0],
  Out_polySurface734: [0, 0, 0],
  Out_polySurface736: [0, 0, 0],
  Out_polySurface738: [0, 0, 0],
  Out_polySurface740: [0, 0, 0],
  Out_polySurface742: [0, 0, 0],
  Out_polySurface744: [0, 0, 0],
  Out_polySurface746: [0, 0, 0],
  Out_polySurface748: [0, 0, 0],
  Out_polySurface750: [0, 0, 0],
  Out_polySurface752: [0, 0, 0],
  Out_polySurface754: [0, 0, 0],
  Out_polySurface756: [0, 0, 0],
  Out_polySurface758: [0, 0, 0],
  Out_polySurface760: [0, 0, 0],
  Out_polySurface762: [0, 0, 0],
  Out_polySurface764: [0, 0, 0],
  Out_polySurface766: [0, 0, 0],
  Out_polySurface768: [0, 0, 0],
  Out_polySurface770: [0, 0, 0],
  Out_polySurface772: [0, 0, 0],
  Out_polySurface773: [0, 0, 0],
  Out_polySurface774: [0, 0, 0],
  Out_polySurface775: [0, 0, 0],
  Out_polySurface776: [0, 0, 0],
  Out_polySurface777: [0, 0, 0],
  Out_polySurface778: [0, 0, 0],
  Out_polySurface779: [0, 0, 0],
  Out_polySurface780: [0, 0, 0],
  Out_polySurface781: [0, 0, 0],
  Out_polySurface782: [0, 0, 0],
  Out_polySurface783: [0, 0, 0],
  Out_polySurface784: [0, 0, 0],
  Out_polySurface785: [0, 0, 0],
  Out_polySurface786: [0, 0, 0],
  Out_polySurface787: [0, 0, 0],
  Out_polySurface788: [0, 0, 0],
  Out_polySurface789: [0, 0, 0],
  Out_polySurface790: [0, 0, 0],
  Out_polySurface791: [0, 0, 0],
  Out_polySurface792: [0, 0, 0],
  Out_polySurface793: [0, 0, 0],
  Out_polySurface794: [0, 0, 0],
  Out_polySurface795: [0, 0, 0],
  Out_polySurface796: [0, 0, 0],
  Out_polySurface797: [0, 0, 0],
  Out_polySurface798: [0, 0, 0],
  Out_polySurface799: [0, 0, 0],
  Out_polySurface800: [0, 0, 0],
  Out_polySurface801: [0, 0, 0],
  Out_polySurface802: [0, 0, 0],
  Out_polySurface803: [0, 0, 0],
  Out_polySurface804: [0, 0, 0],
  Out_polySurface805: [0, 0, 0],
  Out_polySurface806: [0, 0, 0],
  Out_polySurface807: [0, 0, 0],
  Out_polySurface808: [0, 0, 0],
  Out_polySurface809: [0, 0, 0],
  Out_polySurface810: [0, 0, 0],
  Out_polySurface811: [0, 0, 0],
  Out_polySurface812: [0, 0, 0],
  Out_polySurface813: [0, 0, 0],
  Out_polySurface814: [0, 0, 0],
  Out_polySurface815: [0, 0, 0],
  Out_polySurface816: [0, 0, 0],
  Out_polySurface817: [0, 0, 0],
  Out_polySurface818: [0, 0, 0],
  Out_polySurface819: [0, 0, 0],
  Out_polySurface820: [0, 0, 0],
  Out_polySurface821: [0, 0, 0],
  Out_polySurface822: [0, 0, 0],
  Out_polySurface918: [0, 0, 0],
  Out_polySurface919: [0, 0, 0],
  Out_polySurface1017: [0, 0, 0],
  Out_polySurface1114: [0, 0, 0],
  Out_polySurface1116: [0, 0, 0],
  Out_polySurface1142: [0, 0, 0],
  Out_polySurface1143: [0, 0, 0],
  Out_polySurface1144: [0, 0, 0],
  Out_polySurface1170: [0, 0, 0],
  Out_polySurface1171: [0, 0, 0],
  Out_polySurface1172: [0, 0, 0],
  Out_polySurface1199: [0, 0, 0],
  Out_polySurface1200: [0, 0, 0],
  Out_polySurface1226: [0, 0, 0],
  Out_polySurface1228: [0, 0, 0],
  Out_polySurface1254: [0, 0, 0],
  Out_polySurface1256: [0, 0, 0],
  Out_polySurface1283: [0, 0, 0],
  Out_polySurface1284: [0, 0, 0],
  Out_polySurface1311: [0, 0, 0],
  Out_polySurface1312: [0, 0, 0],
  Out_polySurface1339: [0, 0, 0],
  Out_polySurface1340: [0, 2, 0],
  Out_polySurface1369: [0, 2, 0],
  Out_polySurface1370: [0, 0, 0],
  Out_polySurface1425: [0, 0, 0],
  Out_polySurface1426: [0, 0, 0],
  Out_polySurface1453: [0, 0, 0],
};

const PART_NAMES_CAD = Object.keys(PART_EXPLODE_CAD);

function caixaDe(objeto) {
  return new THREE.Box3().setFromObject(objeto);
}

// União das caixas de várias peças — centraliza âncoras com mais de um nó.
function caixaDeTodas(objetos) {
  return objetos.reduce((caixa, objeto) => caixa.union(caixaDe(objeto)), new THREE.Box3());
}

function assentarPecasPendentes(malha, baseY) {
  PECAS_PENDENTES.forEach((nome) => {
    const peca = malha.getObjectByName(nome);
    if (!peca) return;
    const desnivel = baseY - caixaDe(peca).min.y;
    if (desnivel > 0) peca.position.y += desnivel;
  });
}

/**
 * Assenta a malha no disco, na escala e orientação da cena. Gira em Y porque
 * o eixo longo do CAD é Z e o do modelo procedural é X.
 *
 * @returns {number} Escala aplicada, para converter cotas do modelo em
 *   unidades de cena.
 */
export function assentarMalha(malha, { alturaAlvo, giroY }) {
  const corpo = malha.getObjectByName(PECA_BASE);
  const baseY = caixaDe(corpo || malha).min.y;
  assentarPecasPendentes(malha, baseY);

  const altura = caixaDe(malha).max.y - baseY;
  const escala = altura > 0 ? alturaAlvo / altura : 1;

  malha.rotation.y = giroY;
  malha.scale.setScalar(escala);
  malha.position.y = -baseY * escala;

  return escala;
}

/**
 * Veste cada peça com o material equivalente (PART_MATERIAL_CAD), mesmas
 * instâncias de criarMateriais — tema e raio funcionam sem código extra.
 * Generator: delega a fatia de UV (ver aplicarUvEscaladoPorEtapas) e cede o
 * controle entre peças, espalhando o custo entre frames.
 */
function* vestirMalhaPorEtapas(malha, envMapIntensity, materials, escala) {
  const reserva = new THREE.MeshStandardMaterial({
    color: srgbColor(COR),
    roughness: RUGOSIDADE,
    metalness: 0,
    envMapIntensity,
  });

  const meshes = [];
  malha.traverse((object) => { if (object.isMesh) meshes.push(object); });

  for (let indice = 0; indice < meshes.length; indice += 1) {
    const object = meshes[indice];
    const ehBucha = materials && materials.ferroEscuroBucha
      && PECAS_BUCHA_CAD.includes(object.name);
    // Malha decimada (ver PECAS_BUCHA_CAD): normais quebradas liam preto sob
    // luz direta — recalcula pela geometria real antes da projeção de UV
    // (que também lê `normal`).
    if (ehBucha) object.geometry.computeVertexNormals();
    yield* aplicarUvEscaladoPorEtapas(object.geometry, escala);
    aplicarCorNeutra(object.geometry);

    if (ehBucha) {
      object.material = materials.ferroEscuroBucha;
    } else {
      const material = materials && materials[PART_MATERIAL_CAD[object.name]];
      object.material = material || reserva;
    }

    object.castShadow = true;
    object.receiveShadow = true;

    yield;
  }
}

/**
 * Consome um generator fatiado (ver vestirMalhaPorEtapas) via
 * `requestAnimationFrame`, cedendo o controle ao navegador entre fatias em
 * vez de travar a thread até o modelo inteiro estar pronto (ver
 * docs/specs/cma-transformador-cad-performance.md §4).
 */
function executarEmFatias(gerador, aoTerminar) {
  const passo = () => {
    const resultado = gerador.next();
    if (resultado.done) {
      aoTerminar();
      return;
    }
    requestAnimationFrame(passo);
  };
  passo();
}

// Planta raio animado na base de cada bucha (PECAS_ELETRICIDADE_CAD). Filho
// de `group`, não do `partGroup`, para escalar em metros reais, não na
// escala do partGroup.
function adicionarRaiosEletricidade(group, parts, materials) {
  if (!materials || !materials.raio) return [];

  group.updateMatrixWorld(true);
  return PECAS_ELETRICIDADE_CAD.map((nome) => {
    const peca = parts[nome];
    if (!peca) return null;

    const caixa = caixaDe(peca);
    const altura = caixa.max.y - caixa.min.y;
    const centro = caixa.getCenter(new THREE.Vector3());
    const baseMundo = new THREE.Vector3(centro.x, caixa.min.y, centro.z);

    const bolt = buildCurrentBolt(materials);
    bolt.scale.setScalar(altura > 0 ? altura / RAIO_ALTURA_REFERENCIA : 1);
    bolt.position.copy(group.worldToLocal(baseMundo.clone()));
    group.add(bolt);

    // Base fixa no referencial local da bucha — reaplicada em
    // seguirPecaDoRaio a cada passo da explosão.
    bolt.userData.peca = peca;
    bolt.userData.baseLocalNaPeca = peca.worldToLocal(baseMundo.clone());

    return bolt;
  }).filter(Boolean);
}

// Reaplica a base guardada em adicionarRaiosEletricidade a cada passo da
// explosão: a bucha (peça) só translada (explode é soma de vetor, sem girar
// nem escalar), então o próprio `peca.matrix` (relativo ao mesmo `group` que
// é pai do raio) já leva o ponto local de volta para a posição mundial
// correta — sem herdar a rotação da bucha no raio em si, que gira sozinho
// (ver updateCurrent em CmaAsset3dView.vue).
export function seguirPecaDoRaio(bolt) {
  const { peca, baseLocalNaPeca } = bolt.userData;
  if (!peca || !baseLocalNaPeca) return;
  peca.updateMatrix();
  bolt.position.copy(baseLocalNaPeca).applyMatrix4(peca.matrix);
}

const FLOATING_FAN_RADIUS = 0.7;

const FLOATING_FAN_GAP = 0.4;

const FLOATING_FAN_SPEED = 5.0;

const FLOATING_FAN_COUNT = 4;
const FLOATING_FAN_SPACING = FLOATING_FAN_RADIUS * 2 + FLOATING_FAN_GAP;

const FLOATING_FAN_BANK_POSITION = new THREE.Vector3(
  1.202818850463094, 1.589, 1.8140212803455145,
);

const FLOATING_FAN_BANK_POSITION_2 = new THREE.Vector3(
  1.3498188504630941, 1.585, -3.469978719654486,
);

const FLOATING_FAN_BANKS = [
  { nome: 'FloatingFanBank', posicao: FLOATING_FAN_BANK_POSITION },
  { nome: 'FloatingFanBank2', posicao: FLOATING_FAN_BANK_POSITION_2 },
];

// Material único (mesma cor/rugosidade de reserva do CAD, COR/RUGOSIDADE)
// para ler como metal cru do CAD, não como peça colorida do Transformador 2.
// buildFanUnit só usa chapaClara/flange/borracha — as três chaves apontam
// pro mesmo material.
function materialVentiladorFlutuante(envMapIntensity) {
  const material = new THREE.MeshStandardMaterial({
    color: srgbColor(COR),
    roughness: RUGOSIDADE,
    metalness: 0,
    envMapIntensity,
  });
  return { chapaClara: material, flange: material, borracha: material };
}

/**
 * Planta um banco de ventiladores procedurais avulsos (buildFanUnit), em
 * fileira — não depende de nó nomeado do GLB, que varia entre exports do CAD.
 * As unidades são filhas de um `THREE.Group` só, para o banco contar como
 * UMA peça em `model.parts` (explosão, debug) em vez de recalibrar cada
 * ventilador em separado.
 *
 * @param {number[]} [explode] Direção de explosão do banco (ver PART_EXPLODE_CAD).
 * @returns {THREE.Group} O banco, já filho de `group`.
 */
function criarBancoVentiladores(group, envMapIntensity, nome, posicaoInicial, explode) {
  const material = materialVentiladorFlutuante(envMapIntensity);
  const primeiroOffsetX = -((FLOATING_FAN_COUNT - 1) / 2) * FLOATING_FAN_SPACING;

  const banco = new THREE.Group();
  banco.name = nome;
  banco.position.copy(posicaoInicial);

  for (let i = 0; i < FLOATING_FAN_COUNT; i += 1) {
    const ventilador = buildFanUnit(material, FLOATING_FAN_SPEED, i * 0.41);
    ventilador.scale.setScalar(FAN_SCALE * (FLOATING_FAN_RADIUS / 0.46));
    ventilador.position.x = primeiroOffsetX + i * FLOATING_FAN_SPACING;

    // Sem home/explode próprios — applyExplosion só move filhos com esse
    // contrato; é o `banco` que conta como a peça (ver JSDoc acima).
    banco.add(ventilador);
  }

  banco.userData.home = banco.position.clone();
  banco.userData.explode = new THREE.Vector3().fromArray(explode || [0, 0, 0]);
  group.add(banco);

  return banco;
}

/**
 * Planta os bancos de ventiladores flutuantes (ver FLOATING_FAN_BANKS),
 * cada um na sua posição já calibrada ao vivo pelo painel de debug (ver
 * buildFanBankPanel, CmaAsset3dView.vue).
 *
 * @param {Object} [partExplode] Ver PART_EXPLODE_CAD — direção de explosão
 *   por nome de banco (`FloatingFanBank`/`FloatingFanBank2`).
 * @returns {THREE.Group[]} Os bancos, já filhos de `group`.
 */
export function adicionarVentiladoresFlutuantes(group, envMapIntensity, partExplode = {}) {
  return FLOATING_FAN_BANKS.map(({ nome, posicao }) => (
    criarBancoVentiladores(group, envMapIntensity, nome, posicao, partExplode[nome])
  ));
}

export const ANCHOR_POINT_OFFSET_CAD = {
  buchas: [-0.06, 1.3, -0.41],
  gas: [0, 0, 0],
  temperaturas: [0, 0, 0],
  bolsa: [0.1, 1.2, 0],
  tensao: [-0.48, 0.53, 0.2],
  eletricos: [1, 0.5, -0.5],
  resfriamento: [0, 0, 0],
};

/**
 * Posição ABSOLUTA (não deslocamento, ver ANCHOR_POINT_OFFSET_CAD) usada
 * quando nenhum nó de ANCHORS_CAD.nomes existe na malha atual — garante uma
 * âncora por card mesmo sem os nós antigos (`Wire_M`, `Top_Oil_Temp`,
 * `Online_DGA`). Calibre ao vivo pelo painel de âncoras (buildAnchorPanel,
 * CmaAsset3dView.vue) e cole o resultado aqui.
 */
export const ANCHOR_FALLBACK_POINT_CAD = {
  buchas: [0.7, 9.9, -3.3],
  tensao: [1.8, 7.5, 1.7],
  temperaturas: [-2.94, 7.18, -0.73],
  bolsa: [-3.9, 8.51, -0.9],
  eletricos: [5.29, 3.32, -1.25],
  comutador: [4.99, 1.04, -1.94],
  resfriamento: [1.2, 2.4, 1.8],
  // Provisório: mesma posição de 'eletricos' (ver ANCHOR_HIGHLIGHT_PARTS_CAD)
  // até alguém recalibrar 'gas' com o painel de âncoras.
  gas: [5.29, 3.32, -1.25],
};

/**
 * Peça exata por âncora sem nó próprio no GLB (ver ANCHOR_FALLBACK_POINT_CAD)
 * — sobrepõe a busca por proximidade de destacarPecasDasAncoras. Descoberta
 * com o painel de calibração (buildAnchorPanel, CmaAsset3dView.vue).
 */
export const ANCHOR_HIGHLIGHT_PARTS_CAD = {
  // Provisório: 'gas' (card Gás e Umidade) ainda não tem peça/posição
  // própria confirmada — cai na mesma de 'eletricos' (peça aqui e ponto em
  // ANCHOR_FALLBACK_POINT_CAD.gas) até alguém recalibrar com o painel de
  // âncoras e trocar pelos valores exclusivos.
  gas: ['Out_polySurface556'],
  comutador: ['Out_pCylinder288', 'Out_pCylinder289', 'Out_pCylinder290', 'Out_pCylinder291', 'Out_pCylinder292'],
  temperaturas: ['Out_pCylinder242', 'Out_pCylinder243', 'Out_pCylinder244', 'Out_pCylinder245', 'Out_pCylinder246', 'Out_pCylinder247'],
  bolsa: [
    'Out_pCylinder132', 'Out_pCylinder133', 'Out_pCylinder134',
    'Out_polySurface1340', 'Out_polySurface1369', 'Out_pCylinder303',
    'Out_polySurface721', 'Out_polySurface722',
  ],
  eletricos: ['Out_polySurface556'],
};

function vetorDaTabela(tabela, id) {
  return new THREE.Vector3().fromArray(tabela[id] || [0, 0, 0]);
}

/**
 * Monta a âncora com `basePoint` (vindo da malha) e `ajuste` (editável) além
 * de `point` — applyAnchorAdjust (CmaAsset3dView.vue) só refaz
 * `point = basePoint + ajuste`.
 *
 * @param {string} tabela Nome da constante de origem de `ajuste`, só para o
 *   `console.log` do painel de calibração.
 */
function montarAncora(id, part, basePoint, ajuste, tabela) {
  return {
    id,
    part,
    basePoint,
    ajuste,
    tabela,
    point: basePoint.clone().add(ajuste),
  };
}

// Raio de busca da peça sob a âncora (ver destacarPecasDasAncoras) — menor
// acende só um nó solto, maior acende meio transformador.
const ANCHOR_HIGHLIGHT_RADIUS = 0.75;

// Teto de peças por âncora — sem isso, uma região densa (aletas) acenderia o realce inteiro.
const ANCHOR_HIGHLIGHT_MAX_PARTS = 40;

/**
 * Centro e raio de cada peça de `model.parts`, em coordenadas locais do
 * `group`. Fica em `model.partCenters` porque `setFromObject` nos 697 nós
 * não é de graça — calcula uma vez e a recalibração ao vivo reaproveita.
 */
export function medirPecas(model, group) {
  const caixa = new THREE.Box3();
  const tamanho = new THREE.Vector3();

  return Object.keys(model.parts).map((nome) => {
    const peca = model.parts[nome];
    caixa.setFromObject(peca);
    if (caixa.isEmpty()) return null;
    const centro = group.worldToLocal(caixa.getCenter(new THREE.Vector3()));
    return {
      nome, peca, centro, raio: caixa.getSize(tamanho).length() / 2,
    };
  }).filter(Boolean);
}

/** Distância até a BOLA da peça, não o centro — evita que um parafuso perto
 *  do centro vença uma aleta comprida. */
function distanciaAtePeca(medida, ponto) {
  return Math.max(0, medida.centro.distanceTo(ponto) - medida.raio);
}

/**
 * Guarda em `anchor.destaque` as peças que `highlightPart` deve acender pra
 * cada âncora (ver anchorHighlightParts, CmaAsset3dView.vue) — separado de
 * `anchor.part`, que é só a referência de posição do card. Duas fontes:
 * ANCHOR_HIGHLIGHT_PARTS_CAD (nome calibrado à mão) tem prioridade sempre que
 * resolve pra nó real na malha atual; sem entrada lá (ou nó sumido), busca
 * por proximidade — só possível pras âncoras fallback, com ponto calibrado
 * em ANCHOR_FALLBACK_POINT_CAD.
 *
 * @param {THREE.Group} group Raiz do modelo, com a matriz de mundo em dia.
 */
export function destacarPecasDasAncoras(model, group) {
  model.anchors.forEach((anchor) => {
    const explicitas = ANCHOR_HIGHLIGHT_PARTS_CAD[anchor.id];
    if (explicitas) {
      const pecas = explicitas.map(nome => model.parts[nome]).filter(Boolean);
      if (pecas.length) {
        anchor.destaque = pecas;
        return;
      }
      // Nó listado sumiu desta malha — cai pra busca por proximidade abaixo.
    }

    if (anchor.tabela !== 'ANCHOR_FALLBACK_POINT_CAD') return;

    if (!model.partCenters) model.partCenters = medirPecas(model, group);
    if (!model.partCenters.length) return;

    const perto = model.partCenters
      .map(medida => ({ medida, distancia: distanciaAtePeca(medida, anchor.point) }))
      .sort((a, b) => a.distancia - b.distancia);

    const dentro = perto.filter(({ distancia }) => distancia <= ANCHOR_HIGHLIGHT_RADIUS);
    // Nenhuma peça ao alcance: acende a mais próxima mesmo assim.
    const escolhidas = (dentro.length ? dentro : perto.slice(0, 1))
      .slice(0, ANCHOR_HIGHLIGHT_MAX_PARTS);

    anchor.destaque = escolhidas.map(({ medida }) => medida.peca);
  });
}

/** Sobrescreve (ou cria) a âncora `id` — usada por 'resfriamento' e 'gas',
 *  que só existem após a peça montar na cena (ver ANCHORS_CAD). */
function fixarAncora(model, id, part, basePoint) {
  const ancora = montarAncora(
    id, part, basePoint, vetorDaTabela(ANCHOR_POINT_OFFSET_CAD, id), 'ANCHOR_POINT_OFFSET_CAD',
  );
  const indice = model.anchors.findIndex(atual => atual.id === id);
  if (indice >= 0) model.anchors[indice] = ancora;
  else model.anchors.push(ancora);
}

/**
 * Âncoras de card para o CAD, formato de transformadorProcedular.js (`part`
 * pode ser uma LISTA de nós). `point` é o centroide em coordenadas de mundo
 * menos `part.position`, cancelando a soma feita depois por
 * `anchorWorldPosition` (CmaAsset3dView.vue). Sempre devolve uma âncora por
 * id — sem nó na malha, cai no `Object3D` vazio + ANCHOR_FALLBACK_POINT_CAD.
 *
 * @param {THREE.Object3D} malha Raiz do GLB, já assentada e sem pai.
 * @returns {Array} `anchors` no formato do contrato.
 */
export function construirAncoras(malha) {
  malha.updateMatrixWorld(true);

  return ANCHORS_CAD.map(({ id, nomes }) => {
    const partes = (nomes || []).map(nome => malha.getObjectByName(nome)).filter(Boolean);

    if (!partes.length) {
      return montarAncora(
        id,
        new THREE.Object3D(),
        new THREE.Vector3(),
        vetorDaTabela(ANCHOR_FALLBACK_POINT_CAD, id),
        'ANCHOR_FALLBACK_POINT_CAD',
      );
    }

    const centro = caixaDeTodas(partes).getCenter(new THREE.Vector3());
    return montarAncora(
      id,
      partes.length === 1 ? partes[0] : partes,
      centro.sub(partes[0].position),
      vetorDaTabela(ANCHOR_POINT_OFFSET_CAD, id),
      'ANCHOR_POINT_OFFSET_CAD',
    );
  });
}

/**
 * Separa as peças nomeadas do GLB em `partGroup`s próprios (`model.parts`,
 * liga o CAD à explosão genérica de CmaAsset3dView.vue). Decompõe a matriz de
 * MUNDO do nó no `partGroup` antes de zerar o transform local — reparentar
 * direto herdaria só a transformação LOCAL e a peça saltaria de lugar.
 * PART_NAMES_CAD cobre tanto o CAD antigo quanto os 697 nós genéricos do
 * decimate-v4-ventiladores.glb; nome ausente na malha não entra em `parts`.
 *
 * @param {THREE.Object3D} malha Raiz do GLB, já assentada e sem pai.
 * @param {THREE.Group} group Raiz do modelo (espaço de cena).
 * @param {Object} partExplode Ver PART_EXPLODE_CAD.
 * @returns {Object} `parts` no formato do contrato.
 */
export function construirPartes(malha, group, partExplode) {
  malha.updateMatrixWorld(true);
  const parts = {};

  PART_NAMES_CAD.forEach((nome) => {
    const no = malha.getObjectByName(nome);
    if (!no) return;

    const partGroup = new THREE.Group();
    partGroup.name = nome;
    partGroup.matrix.copy(no.matrixWorld);
    partGroup.matrix.decompose(partGroup.position, partGroup.quaternion, partGroup.scale);
    group.add(partGroup);

    partGroup.add(no);
    no.position.set(0, 0, 0);
    no.rotation.set(0, 0, 0);
    no.scale.set(1, 1, 1);

    partGroup.userData.home = partGroup.position.clone();
    partGroup.userData.explode = new THREE.Vector3().fromArray(partExplode[nome] || [0, 0, 0]);

    parts[nome] = partGroup;
  });

  return parts;
}

const cenaVestidaCache = new Map();
const cenaVestidaTiming = new Map();
const gltfBrutoCache = new Map();
const gltfBrutoProgressListeners = new Map();

function contarTriangulos(objeto) {
  let total = 0;
  objeto.traverse((filho) => {
    if (!filho.isMesh) return;
    const { geometry } = filho;
    const posicao = geometry.getAttribute('position');
    if (!posicao) return;
    total += geometry.index ? geometry.index.count / 3 : posicao.count / 3;
  });
  return total;
}

/** Métricas de carregamento da última vez que `url` foi baixada — ver
 *  cenaVestidaTiming. `null` antes do primeiro carregamento terminar. */
export function obterTimingCarregamento(url) {
  return cenaVestidaTiming.get(url) || null;
}

/**
 * Baixa e faz o parse do `.glb` sem aplicar materiais — separado de
 * carregarCenaVestida para disparar ANTES dos materiais existirem (ver
 * prefetchModeloCad), sobrepondo download e CPU (ver
 * docs/specs/cma-transformador-cad-performance.md §2).
 */
function carregarGltfBruto(url, aoProgredir) {
  if (aoProgredir) {
    if (!gltfBrutoProgressListeners.has(url)) {
      gltfBrutoProgressListeners.set(url, new Set());
    }
    gltfBrutoProgressListeners.get(url).add(aoProgredir);
  }

  if (!gltfBrutoCache.has(url)) {
    cenaVestidaTiming.set(url, {
      duracaoMs: null,
      bytesCarregados: 0,
      bytesTotais: 0,
      triangulos: 0,
      inicio: performance.now(),
    });
    gltfBrutoCache.set(url, new Promise((resolve, reject) => {
      new GLTFLoader()
        .setDRACOLoader(dracoLoader)
        .setMeshoptDecoder(MeshoptDecoder)
        .load(
          url,
          (gltf) => {
            gltfBrutoProgressListeners.delete(url);
            resolve(gltf.scene);
          },
          (evento) => {
            const timing = cenaVestidaTiming.get(url);
            if (timing) {
              timing.bytesCarregados = evento.loaded;
              timing.bytesTotais = evento.total;
            }
            (gltfBrutoProgressListeners.get(url) || []).forEach(ouvinte => ouvinte(evento));
          },
          reject,
        );
    }));
  }

  return gltfBrutoCache.get(url);
}

/**
 * Dispara o download do `.glb` sem esperar materiais (ver carregarGltfBruto).
 * Chamado o mais cedo possível no mount, para rede e CPU correrem em
 * paralelo; `carregarCenaVestida` reaproveita a mesma promise depois.
 */
export function prefetchModeloCad(spec) {
  if (spec && spec.mesh) carregarGltfBruto(spec.mesh.url);
}

function carregarCenaVestida(spec, envMapIntensity, materials, aoProgredir) {
  const { url } = spec.mesh;
  const cenaBrutaPromise = carregarGltfBruto(url, aoProgredir);

  if (!cenaVestidaCache.has(url)) {
    cenaVestidaCache.set(url, cenaBrutaPromise.then(cenaBruta => new Promise((resolve) => {
      const escala = assentarMalha(cenaBruta, spec.mesh);
      executarEmFatias(
        vestirMalhaPorEtapas(cenaBruta, envMapIntensity, materials, escala),
        () => {
          const timing = cenaVestidaTiming.get(url);
          if (timing) {
            timing.duracaoMs = performance.now() - timing.inicio;
            timing.triangulos = contarTriangulos(cenaBruta);
          }
          resolve(cenaBruta);
        },
      );
    })));
  }

  return cenaVestidaCache.get(url);
}

/**
 * Cria o modelo do CAD para um slot do carrossel. Devolve o modelo na hora,
 * com o grupo vazio (precisa existir de forma síncrona), e enche o grupo
 * quando o GLB chega.
 *
 * @param {Object} spec Entrada de TRANSFORMER_MODELS (`mesh.url`, `mesh.alturaAlvo`).
 * @param {Object} contexto
 * @param {Object} contexto.materials Materiais da cena (criarMateriais).
 * @param {number} contexto.envMapIntensity Intensidade de ambiente do tema.
 * @param {number} contexto.deslocamentoX Deslocamento do ativo no palco.
 * @param {Function} contexto.seguirVivo Diz se o viewer ainda existe quando o GLB chega.
 * @param {Function} [contexto.aoProgredir] Progresso do `GLTFLoader`
 *   (`{loaded, total}`); `total` é `0` sem `Content-Length` no servidor.
 * @param {Function} contexto.aoErrar Recebe a falha de download.
 * @param {Function} contexto.aoMontar Avisa que a malha entrou na cena,
 *   com as métricas de carregamento (ver obterTimingCarregamento).
 * @param {boolean} [contexto.adiarCarregamento] Quando `true`, não inicia o
 *   `GLTFLoader` — o modelo volta com `iniciarCarregamento()` para o
 *   chamador disparar depois (evita triplicar o parse quando 3 variantes
 *   do carrossel usam a mesma malha — ver CmaAsset3dView.vue).
 * @returns {Object} Modelo no formato de contrato.js.
 */
export function criarModeloCad(spec, {
  materials, envMapIntensity, deslocamentoX, seguirVivo, aoProgredir, aoErrar, aoMontar,
  adiarCarregamento,
}) {
  const group = new THREE.Group();
  group.position.x = deslocamentoX;

  const model = {
    id: spec.id,
    group,
    parts: {},
    anchors: [],
    helices: [],
    ponteiros: [],
    raios: [],
  };

  // Roda sobre um CLONE da cena-base já vestida (ver carregarCenaVestida) —
  // materiais/UV compartilhados por referência; só a hierarquia daqui pra
  // baixo é exclusiva desta instância.
  function montarNaCena(cena) {
    if (!seguirVivo()) return;

    model.anchors = construirAncoras(cena);
    model.parts = construirPartes(cena, group, PART_EXPLODE_CAD);
    group.add(cena);
    model.raios = adicionarRaiosEletricidade(group, model.parts, materials);

    const bancosVentiladores = adicionarVentiladoresFlutuantes(
      group, envMapIntensity, PART_EXPLODE_CAD,
    );
    bancosVentiladores.forEach((banco) => {
      model.parts[banco.name] = banco;
      banco.traverse((objeto) => {
        if (objeto.userData.giro) model.helices.push(objeto);
      });
    });

    // `part` recebe TODOS os bancos (não só o primeiro) — anchorParts (CmaAsset3dView.vue)
    // aceita array, e o card usa a posição do banco [0] só como referência.
    if (bancosVentiladores.length) {
      fixarAncora(model, 'resfriamento', bancosVentiladores, new THREE.Vector3());
    }

    group.updateMatrixWorld(true);
    destacarPecasDasAncoras(model, group);

    aoMontar(obterTimingCarregamento(spec.mesh.url));
  }

  function iniciarCarregamento() {
    carregarCenaVestida(spec, envMapIntensity, materials, aoProgredir).then((cenaBase) => {
      if (!seguirVivo()) return;

      montarNaCena(cenaBase.clone());
    }, aoErrar);
  }

  if (adiarCarregamento) {
    model.iniciarCarregamento = iniciarCarregamento;
  } else {
    iniciarCarregamento();
  }

  return model;
}

export default criarModeloCad;
